/** @format */
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useBrand } from "../hooks/useBrand";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { FLAG_IMPORT_CSV_FEATURE, FLAG_SUBJECT_EXPERT_APPROVAL_QUEUE } from "../featureFlags/constants";
import { BRANDS, isSubjectMatterExpert } from "@question-bank/shared";
import { useTranslation } from "react-i18next";
import { Locale } from "../features/i18n/init";

const disabledNavItems = ["Create Question", "Review", "Settings"];
const getNavLinkClassName = ({ isActive }: { isActive: boolean }) =>
	[
		"flex items-center rounded-lg px-4 py-3 text-sm font-semibold transition-colors duration-200",
		"whitespace-nowrap",
		isActive
			? "bg-[var(--admin-color-primary-strong)] text-white shadow-[var(--admin-shadow-active)]"
			: "text-slate-700 hover:bg-[var(--admin-color-accent-bg)] hover:text-[var(--admin-color-primary-strong)]",
	].join(" ");

export const AdminLayout = () => {
	const { logout, user } = useAuth();
	const { brand } = useBrand();
	const canShowImportQuestions = useFeatureFlag(FLAG_IMPORT_CSV_FEATURE);
	const canShowApprovalQueue = useFeatureFlag(FLAG_SUBJECT_EXPERT_APPROVAL_QUEUE);
	const { t } = useTranslation();
	const isExpert = isSubjectMatterExpert(user?.group);
	const navigate = useNavigate();
	const fullName = [user?.given_name, user?.family_name].filter(Boolean).join(" ").trim();
	const displayName = fullName || user?.email || "Staff user";
	const brandLabel = BRANDS[brand].name;
	const { locale } = useParams<{ locale: Locale }>();

	const handleSignOut = async () => {
		await logout();
		navigate(`/${locale}/`, { replace: true });
	};

	return (
		<div className="min-h-screen bg-(--admincolor-page-bg) text-slate-900">
			<header className="border-b border-white bg-(--admin-color-primary-strong) text-white">
				<div className="mx-auto flex max-w-[1440px] flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
					<div className=" flex flex-col gap-4  md:flex-row md:items-center md:justify-between">
						<div>
							<div className="flex items-center gap-3">
								<h1 className=" text-[1.75rem] font-bold leading-none">Question Bank </h1>
								<span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase [tracking-0.12em]">
									Admin
								</span>
							</div>
							<p className="mt-2 text-sm text-white/85"> {brandLabel} staff area </p>
						</div>

						<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
							<div className="rounded-lg border border-white/20 bg-white/10 px-4 py-3">
								<p className="text-xs font-semibold uppercase tracking-[0.12em] text-white/75">Signed in as</p>
								<p className="text-sm font-semibold text-white">{displayName}</p>
							</div>
							<button
								type="button"
								onClick={() => {
									void handleSignOut();
								}}
								className="rounded-lg border border-white/30 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/20"
							>
								Sign out
							</button>
						</div>
					</div>
				</div>
			</header>

			<div className="mx-auto mt-10 flex w-full max-w-[1400px] flex-col gap-10 px-4 py-6 sm:px-6 lg:flex-row lg:px-8">
				<aside className="w-full md:w-[240px] md:flex-none">
					<nav
						aria-label="Admin navigation"
						className="flex gap-2 overflow-x-auto rounded-lg bg-white p-3 shadow-(--admin-shadow-raised) lg:flex-col lg:overflow-visible"
					>
						<NavLink to={`/${locale}/admin/questions`} end className={getNavLinkClassName}>
							Questions
						</NavLink>

						{canShowApprovalQueue && isExpert && (
							<NavLink to={`/${locale}/admin/questions?status=staged`} className={getNavLinkClassName}>
								{t("approvalQueue.nav")}
							</NavLink>
						)}

						<NavLink to={`/${locale}/admin/papers`} end className={getNavLinkClassName}>
							Papers
						</NavLink>

						{canShowImportQuestions && (
							<>
								<NavLink to={`/${locale}/admin/import`} end className={getNavLinkClassName}>
									Import Questions
								</NavLink>

								<NavLink to={`/${locale}/admin/import-jobs`} end className={getNavLinkClassName}>
									Manage Imports
								</NavLink>
							</>
						)}

						{disabledNavItems.map((item) => (
							<span
								key={item}
								aria-disabled="true"
								className="flex cursor-not-allowed items-center rounded-lg bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-400"
							>
								{item}
							</span>
						))}
					</nav>
				</aside>
				<main className="min-w-0 flex-1">
					<Outlet />
				</main>
			</div>
		</div>
	);
};
